import { AxiosError } from 'axios'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import axiosClient, { getRefreshToken, getUserName } from '~/api/axiosClient'
import { store } from '~/app/store'
import { REFRESH_TOKEN_API } from '~/constants/APIs'
import { ALL_NS } from '~/i18n/namespaces'
import { IAuthState } from '~/models'
import { setUser } from '~/redux/features'

interface IArgs {
  presignedUploadUrl: string
  files: File[]
  onceTimeUpload?: boolean
}

export interface IFileUploadErrorMessage {
  fileName?: string
  message: string
}

export const useFileUploadMutation = () => {
  const { t } = useTranslation(ALL_NS)
  const [progress, setProgress] = useState(0)
  const [uploadingFileCount, setUploadingFileCount] = useState(0)
  const [uploadMessages, setUploadMessages] = useState<IFileUploadErrorMessage[]>([])
  const [isError, setIsError] = useState<boolean>(false)
  const [responseData, setResponseData] = useState<any>()

  const abortControllersRef = useRef<AbortController[]>([]) // Use ref to persist abort controllers
  const isAbortedRef = useRef(false) // Ref to track if the upload is aborted

  const handleAbort = () => {
    isAbortedRef.current = true // Mark the upload process as aborted
    abortControllersRef.current.forEach(controller => controller.abort())
    abortControllersRef.current = [] // Clear the abort controllers
    setProgress(0)
  }

  const setFileMessage = (fileName: string | undefined, message: string) => {
    setUploadMessages(prevState => [...prevState, { fileName, message }])
  }

  const uploadFile = async (args: IArgs, file: File, retries = 3): Promise<void> => {
    if (isAbortedRef.current) return // If aborted, stop the upload
    const controller = new AbortController()
    abortControllersRef.current.push(controller)
    // setAbortControllers(prevState => [...prevState, controller])
    const formData = new FormData()
    if (args.onceTimeUpload) {
      args.files.forEach(f => formData.append('formFiles', f))
    } else {
      formData.set('formFiles', file)
    }

    try {
      const res = await axiosClient.post(args.presignedUploadUrl, formData, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: ev => {
          const fileProgress = Math.round((ev.loaded * 100) / (ev.total ?? 1))
          setProgress(prevProgress =>
            prevProgress < 100 && fileProgress > prevProgress
              ? fileProgress
              : prevProgress
          )
        }
      })
      setResponseData(res)
      setIsError(false)

      if (args.onceTimeUpload && (res as any).messages) {
        setFileMessage(undefined, (res as any).messages.join('\r\n'))
      } else if (!args.onceTimeUpload && (res as any).messages) {
        const originalMessages = (res as any).messages as string[]
        const newMessages = [t('SuccessfullyUploadedFile_tk0_', { 0: file.name }), originalMessages]
        setFileMessage(undefined, newMessages.join('\r\n'))
      }
      else {
        setFileMessage(undefined, t('SuccessfullyUploadedFile_tk0_', { 0: file.name }))
      }
    } catch (err: any) {
      // Refresh the access token and retry uploading if got network error
      if (err.code === 'ERR_NETWORK' && retries > 0) {
        console.warn(`Retrying upload for ${file.name}, attempts left: ${retries}`)
        const userName = getUserName()
        const refreshToken = getRefreshToken()

        const rs = (await axiosClient.post(REFRESH_TOKEN_API, {
          userName: userName,
          refreshToken: refreshToken
        })) as unknown as IAuthState

        store.dispatch(setUser(rs))

        await new Promise(resolve => setTimeout(resolve, 2000)) // wait for 2 seconds before retry
        return await uploadFile(args, file, retries - 1) // Retry the upload
      }
      setIsError(true)
      setResponseData(null)

      if (err.data) {
        setFileMessage(file.name, err.data?.messages?.join('\n'))
        return
      }
    }
    setProgress(0)
  }

  const mutation = useMutation<void, AxiosError, IArgs>(async args => {
    setUploadMessages([])
    setUploadingFileCount(1)
    abortControllersRef.current = [] // Reset the abort controllers at the start of the process
    isAbortedRef.current = false // Reset the aborted flag

    if (args.onceTimeUpload) {
      await uploadFile(args, args.files[0])
      return
    }

    // Upload files one by one
    for (const file of args.files) {
      if (isAbortedRef.current) break // Stop the loop if aborted
      await uploadFile(args, file)
      setUploadingFileCount(prevCount => prevCount + 1)
    }
  })

  return {
    ...mutation,
    progress,
    isError,
    handleAbort,
    uploadMessages,
    uploadingFileCount,
    responseData
  }
}
